import React, { FC, useRef, useState } from "react";
import {
    CheckBoxGroup,
    ErrorAlert,
    FieldError,
    FieldLabel,
    FieldSet,
    FormAction,
    FormCard,
    FormCardBody,
    FormCardHead,
    FormItem,
    FormRow,
    Legend,
    PhoneGroup,
    PrivacyNote,
    SuccessBox,
    SuccessIcon,
    UploadBox,
    UploadFileName,
    UploadHint,
    UploadTitle,
} from "./style";
import { Button, CheckBox, CustomInput, CustomSelect, CustomTextArea } from "../../../../common/components";
import NursingLeadService from "../../../../services/nursing-lead.service";
import {
    countryCodeOptions,
    departmentOptions,
    experienceOptions,
    visaStatusOptions,
    workedCountryOptions,
    yesNoOptions,
} from "../../constants/formOptions";

const MAX_CV_BYTES = 10 * 1024 * 1024;

const ApplicationForm: FC = () => {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formError, setFormError] = useState("");

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [countryCode, setCountryCode] = useState("");
    const [phone, setPhone] = useState("");
    const [nzRegistered, setNzRegistered] = useState("");
    const [experienceYears, setExperienceYears] = useState("");
    const [workedCountries, setWorkedCountries] = useState<string[]>([]);
    const [department, setDepartment] = useState("");
    const [inNewZealand, setInNewZealand] = useState("");
    const [visaStatus, setVisaStatus] = useState("");
    const [nzDriverLicence, setNzDriverLicence] = useState("");
    const [currentAddress, setCurrentAddress] = useState("");
    const [cvFile, setCvFile] = useState<File | null>(null);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const cardRef = useRef<HTMLDivElement>(null);

    const toggleWorkedCountry = (country: string, checked: boolean) => {
        setWorkedCountries((prev) =>
            checked ? [...prev, country] : prev.filter((c) => c !== country)
        );
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        setCvFile(file);
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};

        if (!name.trim()) newErrors.name = "This field is required";
        if (!email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^\S+@\S+\.\S+$/.test(email)) {
            newErrors.email = "Invalid email address";
        }
        if (!countryCode) newErrors.countryCode = "Country code is required";
        if (!phone.trim()) newErrors.phone = "This field is required";
        if (!nzRegistered) newErrors.nzRegistered = "This field is required";
        if (!experienceYears) newErrors.experienceYears = "This field is required";
        if (workedCountries.length === 0) newErrors.workedCountries = "Select at least one country";
        if (!department) newErrors.department = "This field is required";
        if (!inNewZealand) newErrors.inNewZealand = "This field is required";

        if (inNewZealand === "Yes") {
            if (!visaStatus) newErrors.visaStatus = "This field is required";
            if (!nzDriverLicence) newErrors.nzDriverLicence = "This field is required";
            if (!currentAddress.trim()) newErrors.currentAddress = "This field is required";
        }

        if (!cvFile) {
            newErrors.cv = "Please upload your CV in PDF format";
        } else {
            const isPdf = cvFile.type === "application/pdf" || /\.pdf$/i.test(cvFile.name);
            if (!isPdf) {
                newErrors.cv = "Only PDF files are accepted for the CV";
            } else if (cvFile.size > MAX_CV_BYTES) {
                newErrors.cv = "The CV file must be 10 MB or smaller";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        setFormError("");

        if (!validate()) {
            setFormError("Please complete all required fields correctly.");
            return;
        }

        const formData = new FormData();
        formData.append("name", name.trim());
        formData.append("email", email.trim());
        formData.append("country_code", countryCode.trim());
        formData.append("phone", phone.trim());
        formData.append("nz_registered", nzRegistered);
        formData.append("experience_years", experienceYears);
        workedCountries.forEach((country) => formData.append("worked_country[]", country));
        formData.append("department", department);
        formData.append("in_new_zealand", inNewZealand);
        if (inNewZealand === "Yes") {
            formData.append("visa_status", visaStatus);
            formData.append("nz_driver_licence", nzDriverLicence);
            formData.append("current_address", currentAddress.trim());
        }
        if (cvFile) {
            formData.append("cv", cvFile);
        }

        setSubmitting(true);
        NursingLeadService.submit(formData)
            .then((data) => {
                if (data && data.status !== false) {
                    setSubmitted(true);
                    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                } else {
                    setFormError((data && data.message) ? data.message : "Something went wrong. Please try again.");
                }
            })
            .catch((error) => {
                const serverMessage = error?.response?.data?.message;
                setFormError(serverMessage || "Network error. Please check your connection and try again.");
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <FormCard id="application-form" ref={cardRef}>
            <FormCardHead>
                <h3>Nurse Career Application</h3>
                <p>Fields marked with <span>*</span> are required.</p>
            </FormCardHead>
            {submitted ? (
                <SuccessBox>
                    <SuccessIcon>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.2" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                    </SuccessIcon>
                    <h3>Thank you for your submission.</h3>
                    <p>Our team will review your details and contact you soon to guide you through the next steps of your New Zealand nursing journey.</p>
                </SuccessBox>
            ) : (
                <FormCardBody>
                    {formError ? (
                        <ErrorAlert>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor" width="20" height="20">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                            </svg>
                            <span>{formError}</span>
                        </ErrorAlert>
                    ) : null}

                    <FieldSet>
                        <Legend>Personal Information</Legend>
                        <FormItem>
                            <CustomInput
                                label="Full Name"
                                required
                                id="nc-name"
                                type="text"
                                size="large"
                                placeholder="Enter your full name"
                                inputValue={name}
                                error={errors.name}
                                onChange={(value: string) => setName(value)}
                            />
                        </FormItem>
                        <FormItem>
                            <CustomInput
                                label="Email Address"
                                required
                                id="nc-email"
                                type="email"
                                size="large"
                                placeholder="you@example.com"
                                inputValue={email}
                                error={errors.email}
                                onChange={(value: string) => setEmail(value)}
                            />
                        </FormItem>
                        <FormItem>
                            <PhoneGroup>
                                <div>
                                    <CustomSelect
                                        label="Country Code"
                                        required
                                        size="large"
                                        options={countryCodeOptions}
                                        value={countryCode}
                                        placeholder="Country code"
                                        onChange={(value: string) => setCountryCode(value)}
                                    />
                                    {errors.countryCode ? <FieldError>{errors.countryCode}</FieldError> : null}
                                </div>
                                <div>
                                    <CustomInput
                                        label="Phone Number"
                                        required
                                        id="nc-phone"
                                        type="tel"
                                        size="large"
                                        placeholder="Phone number"
                                        inputValue={phone}
                                        error={errors.phone}
                                        onChange={(value: string) => setPhone(value)}
                                    />
                                </div>
                            </PhoneGroup>
                        </FormItem>
                    </FieldSet>

                    <FieldSet>
                        <Legend>Professional Details</Legend>
                        <FormRow>
                            <FormItem>
                                <CustomSelect
                                    label="NZ Nursing Council Registered Nurse?"
                                    required
                                    size="large"
                                    options={yesNoOptions}
                                    value={nzRegistered}
                                    placeholder="Select an option"
                                    onChange={(value: string) => setNzRegistered(value)}
                                />
                                {errors.nzRegistered ? <FieldError>{errors.nzRegistered}</FieldError> : null}
                            </FormItem>
                            <FormItem>
                                <CustomSelect
                                    label="Years of Nursing Experience"
                                    required
                                    size="large"
                                    options={experienceOptions}
                                    value={experienceYears}
                                    placeholder="Select experience"
                                    onChange={(value: string) => setExperienceYears(value)}
                                />
                                {errors.experienceYears ? <FieldError>{errors.experienceYears}</FieldError> : null}
                            </FormItem>
                        </FormRow>
                        <FormItem>
                            <FieldLabel>In which country have you worked as a Registered Nurse? <span>*</span></FieldLabel>
                            <CheckBoxGroup className={errors.workedCountries ? "wc-invalid" : ""}>
                                {workedCountryOptions.map((country) => (
                                    <CheckBox
                                        key={country}
                                        label={country}
                                        checked={workedCountries.includes(country)}
                                        onChange={(checked: boolean) => toggleWorkedCountry(country, checked)}
                                    />
                                ))}
                            </CheckBoxGroup>
                            {errors.workedCountries ? <FieldError>{errors.workedCountries}</FieldError> : null}
                        </FormItem>
                        <FormItem>
                            <CustomSelect
                                label="Which department/specialty do you have experience in?"
                                required
                                size="large"
                                options={departmentOptions}
                                value={department}
                                placeholder="Select department / specialty"
                                onChange={(value: string) => setDepartment(value)}
                            />
                            {errors.department ? <FieldError>{errors.department}</FieldError> : null}
                        </FormItem>
                    </FieldSet>

                    <FieldSet>
                        <Legend>Your New Zealand Status</Legend>
                        <FormItem>
                            <CustomSelect
                                label="Are you currently in New Zealand?"
                                required
                                size="large"
                                options={yesNoOptions}
                                value={inNewZealand}
                                placeholder="Select an option"
                                onChange={(value: string) => setInNewZealand(value)}
                            />
                            {errors.inNewZealand ? <FieldError>{errors.inNewZealand}</FieldError> : null}
                        </FormItem>
                        {inNewZealand === "Yes" ? (
                            <>
                                <FormRow>
                                    <FormItem>
                                        <CustomSelect
                                            label="What is your visa status?"
                                            required
                                            size="large"
                                            options={visaStatusOptions}
                                            value={visaStatus}
                                            placeholder="Select visa status"
                                            onChange={(value: string) => setVisaStatus(value)}
                                        />
                                        {errors.visaStatus ? <FieldError>{errors.visaStatus}</FieldError> : null}
                                    </FormItem>
                                    <FormItem>
                                        <CustomSelect
                                            label="Do you have a New Zealand driver's licence?"
                                            required
                                            size="large"
                                            options={yesNoOptions}
                                            value={nzDriverLicence}
                                            placeholder="Select an option"
                                            onChange={(value: string) => setNzDriverLicence(value)}
                                        />
                                        {errors.nzDriverLicence ? <FieldError>{errors.nzDriverLicence}</FieldError> : null}
                                    </FormItem>
                                </FormRow>
                                <FormItem>
                                    <CustomTextArea
                                        label="Please mention your current address"
                                        id="nc-address"
                                        size="large"
                                        placeholder="Your current address in New Zealand"
                                        inputValue={currentAddress}
                                        error={errors.currentAddress}
                                        onChange={(value: string) => setCurrentAddress(value)}
                                    />
                                </FormItem>
                            </>
                        ) : null}
                    </FieldSet>

                    <FieldSet>
                        <Legend>Upload Your CV</Legend>
                        <UploadBox className={errors.cv ? "wc-invalid" : ""}>
                            <input type="file" accept="application/pdf,.pdf" onChange={handleFileChange} />
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.6" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                            </svg>
                            <UploadTitle>Upload Resume / CV</UploadTitle>
                            <UploadHint>PDF only &middot; Maximum size 10&nbsp;MB</UploadHint>
                            {cvFile ? <UploadFileName>📄 {cvFile.name}</UploadFileName> : null}
                        </UploadBox>
                        {errors.cv ? <FieldError>{errors.cv}</FieldError> : null}
                    </FieldSet>

                    <FormAction>
                        <Button
                            label="Submit Application"
                            loading={submitting}
                            onClick={() => handleSubmit()}
                            variant="primary"
                            type="button"
                            fullwidth
                        />
                    </FormAction>

                    <PrivacyNote>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.7" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 0h10.5a1.5 1.5 0 0 1 1.5 1.5v6.75a1.5 1.5 0 0 1-1.5 1.5H6.75a1.5 1.5 0 0 1-1.5-1.5V12a1.5 1.5 0 0 1 1.5-1.5Z" />
                        </svg>
                        <span>Your information will only be used for assessing nursing career opportunities and will be handled confidentially.</span>
                    </PrivacyNote>
                </FormCardBody>
            )}
        </FormCard>
    );
};

export default ApplicationForm;
